import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaChevronDown, FaSpinner, FaChevronUp } from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';
import { Copy, Check, SquareCode, MessageSquare } from 'lucide-react';
import { useAiChatApiService } from '../../../../api/providers/AppServiceProvider';
import DynamicFilterInput from '../../../atoms/Custom/DynamicFilterInput/DynamicFilterInput';
import { FilterColumnData } from '../../../../api/services/ai-chat-api-service';
import { useAppSelector } from '../../../../redux/hooks';
import { RootState } from '../../../../redux/store';
import { FilterItem } from '../../../../models/ai-chat/filter-request';
import PDFDownload from './PDFDownloadAction';
import DownloadAction from './DownloadAction';
import MenuAction from './MenuAction';
import { downloadChartAsCSV } from 'utils/chartDataToCSV';
import FilterDrawer from './FilterDrawer';
import Tooltip from '../tooltip';
import { ChatMessage } from '../../../../models/ai-chat/chat.types';
import { format } from 'sql-formatter';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coy } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ChartSwitcher from './ChartSwitcher';
import ChartConfigDrawer from './ChartConfigDrawer';
import { LlmColumn, transformTableToChartData, getDefaultDimensionColumn, getDefaultMeasureColumn, getDimensionColumns } from './tableToChartData';

interface AppliedFilter {
	field: string;
	operator: string;
	value: string | string[];
	displayName: string;
	filterType?: string;
	baseOperator?: string;
	originalField?: string;
}

interface AppliedFiltersProps {
	sqlQuery?: string;
	onFilterValueChange?: (filter: AppliedFilter, newValue: string) => void;
	onFilterUpdateComplete?: (updatedData: any) => void;
	onFilterDataChange?: (filterData: FilterItem[]) => void;
	customerId?: string;
	filterData?: FilterItem[];
	messageId?: string;
	hasChartSwitcher?: boolean;
	pdfGeneration?: boolean;
	onExportExcel?: () => void;
	onDownloadPDF?: () => void;
	onFullView?: () => void;
	chartData?: any;
	chartType?: string;
	chartMetadata?: any;
	isInModal?: boolean;
	tableData?: {
		data: any;
		sortConfig?: { column: number; direction: 'asc' | 'desc' } | null;
		onSort?: (columnIndex: number) => void;
		filteredAndSortedRows?: any[][];
		isNumericColumn?: (columnIndex: number) => boolean;
		formatCellValue?: (cell: any, columnIndex?: number) => any;
		onDataUpdate?: (newData: any) => void;
	};
	tableRequest?: any;
	onTableFullView?: () => void;
	isTableFullViewOpen?: boolean;
	onInsightsLoadingChange?: (isLoading: boolean) => void;
	message?: ChatMessage;
	showChartSwitcher?: boolean;
	chartSwitcherDefaultType?: string;
	chartSwitcherCurrentType?: string;
	chartSwitcherData?: any;
	onChartTypeChange?: (newType: string) => void;
	onDrawerOpen?: () => void;
	onFilterDrawerOpen?: (drawerProps: {
		availableColumns: string[];
		filterDataMap: Record<string, FilterColumnData>;
		existingFilters: any[];
		onApply: (filters: any[]) => void;
		isLoading: boolean;
	}) => void;
	onChartFullViewOpen?: (fullViewData: {
		data: any;
		type: string;
		sqlQuery?: string;
		messageId?: string;
		metadata: any;
		filterData?: any[];
		customerId?: string;
	}) => void;
	onTableFullViewOpen?: (fullViewData: {
		data: any;
		sqlQuery?: string;
		messageId?: string;
		filterData?: any[];
		customerId?: string;
		llmPayload?: any;
	}) => void;
	vizKey?: string;
	llmPayload?: {
		columns?: LlmColumn[];
		visual_config?: any;
	};
	onChartTypeChangeWithData?: (newType: string, chartData: any) => void;
}

const AppliedFiltersDisplay: React.FC<AppliedFiltersProps> = ({
	sqlQuery,
	onFilterValueChange,
	onFilterUpdateComplete,
	onFilterDataChange,
	customerId,
	filterData,
	messageId,
	hasChartSwitcher = false,
	pdfGeneration = false,
	onExportExcel,
	onDownloadPDF,
	onFullView,
	chartData,
	chartType,
	chartMetadata,
	isInModal = false,
	tableData,
	tableRequest,
	onTableFullView,
	isTableFullViewOpen = false,
	onInsightsLoadingChange,
	message,
	showChartSwitcher = false,
	chartSwitcherDefaultType,
	chartSwitcherCurrentType,
	chartSwitcherData,
	onChartTypeChange,
	onDrawerOpen,
	onFilterDrawerOpen,
	onChartFullViewOpen,
	onTableFullViewOpen,
	vizKey,
	llmPayload,
	onChartTypeChangeWithData,
}) => {
	// Debug: Log received filterData
	// console.log('AppliedFiltersDisplay received filterData:', filterData, 'length:', filterData?.length);

	// Note: appliedFilters is computed from sqlQuery, not from props
	// The filterData prop is used for passing data to parent components
	const aiChatApiService = useAiChatApiService();
	const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
	const [filterDataMap, setFilterDataMap] = useState<Record<string, FilterColumnData>>({});
	const [loadingFilters, setLoadingFilters] = useState<Record<string, boolean>>({});
	const [updatingFilter, setUpdatingFilter] = useState<string | null>(null);
	const [showAllFilters, setShowAllFilters] = useState<boolean>(false);
	const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);
	const [availableColumns, setAvailableColumns] = useState<string[]>([]);
	const [loadingColumns, setLoadingColumns] = useState<boolean>(false);
	const [isApplyingFilters, setIsApplyingFilters] = useState<boolean>(false);
	const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [isCopied, setIsCopied] = useState<boolean>(false);
	const [showDetails, setShowDetails] = useState<boolean>(false);

	// ChartConfigDrawer state for table-to-chart conversion
	const [isChartConfigDrawerOpen, setIsChartConfigDrawerOpen] = useState<boolean>(false);
	const [selectedChartType, setSelectedChartType] = useState<string>('table');
	const [selectedDimension, setSelectedDimension] = useState<string>('');
	const [selectedSecondaryDimension, setSelectedSecondaryDimension] = useState<string>('');
	const [selectedMeasures, setSelectedMeasures] = useState<string[]>([]);
	const originalTableDataRef = useRef<{ headers: string[]; rows: any[][] } | null>(null);

	const getSourceTableData = (): { headers: string[]; rows: any[][] } | null => {
		if (tableData?.data?.headers && tableData?.data?.rows) {
			if (!originalTableDataRef.current) {
				originalTableDataRef.current = {
					headers: tableData.data.headers,
					rows: tableData.data.rows
				};
			}
			return originalTableDataRef.current;
		}
		if (chartData?.headers && chartData?.rows && chartType === 'table') {
			if (!originalTableDataRef.current) {
				originalTableDataRef.current = {
					headers: chartData.headers,
					rows: chartData.rows
				};
			}
			return originalTableDataRef.current;
		}
		return originalTableDataRef.current;
	};

	const hasTableSourceData = (): boolean => {
		return !!getSourceTableData() || !!(tableData?.data) || (!!chartData?.headers && !!chartData?.rows && chartType === 'table');
	};

	React.useEffect(() => {
		if (tableData?.data?.headers && tableData?.data?.rows) {
			originalTableDataRef.current = {
				headers: tableData.data.headers,
				rows: tableData.data.rows
			};
		} else if (chartData?.headers && chartData?.rows && chartType === 'table') {
			originalTableDataRef.current = {
				headers: chartData.headers,
				rows: chartData.rows
			};
		}
	}, [tableData, chartData, chartType]);

	React.useEffect(() => {
		if (chartType && chartType !== 'table') {
			setSelectedChartType(chartType);
		} else if (chartType === 'table') {
			setSelectedChartType('table');
		}
	}, [chartType]);

	React.useEffect(() => {
		if (llmPayload?.columns && llmPayload.columns.length > 0) {
			const defaultDimension = getDefaultDimensionColumn(llmPayload.columns);
			const defaultMeasure = getDefaultMeasureColumn(llmPayload.columns);
			if (defaultDimension && !selectedDimension) {
				setSelectedDimension(defaultDimension);
			}
			if (defaultMeasure && selectedMeasures.length === 0) {
				setSelectedMeasures([defaultMeasure]);
			}
		}
	}, [llmPayload]);

	// Initialize filterDataMap with date filters from metadata
	React.useEffect(() => {
		if (chartMetadata?.timeframe_start_date || chartMetadata?.timeframe_end_date) {
			const newFilterDataMap: Record<string, FilterColumnData> = {};

			if (chartMetadata.timeframe_start_date) {
				newFilterDataMap['timeframe_start_date'] = {
					field_name: 'timeframe_start_date',
					data_type: 'date',
					suggestion: 'datepicker',
					values: [],
					operators: [
						{ value: '>=', label: 'Greater than or equal to' },
						{ value: '<=', label: 'Less than or equal to' },
						{ value: '=', label: 'Equals' }
					]
				};
			}

			if (chartMetadata.timeframe_end_date) {
				newFilterDataMap['timeframe_end_date'] = {
					field_name: 'timeframe_end_date',
					data_type: 'date',
					suggestion: 'datepicker',
					values: [],
					operators: [
						{ value: '>=', label: 'Greater than or equal to' },
						{ value: '<=', label: 'Less than or equal to' },
						{ value: '=', label: 'Equals' }
					]
				};
			}

			setFilterDataMap((prev) => ({
				...prev,
				...newFilterDataMap
			}));
		}
	}, [chartMetadata]);

	const { messages } = useAppSelector((state: RootState) => state.chat);

	// Configuration for "See more" functionality
	const MAX_VISIBLE_FILTERS = 3; // Show first 6 filters before "See more"

	const getCurrentMessageId = (): string | undefined => {
		const assistantMessages = messages.filter((msg) => msg.role === 'assistant' && !msg.pending);
		if (assistantMessages.length > 0) {
			const latestAssistantMessage = assistantMessages[assistantMessages.length - 1];
			return latestAssistantMessage.message_id;
		}
		return undefined;
	};

	const getOriginalUserQuestion = (): string => {
		const userMessages = messages.filter((msg) => msg.role === 'user');
		return userMessages.length > 0 ? userMessages[0].content : '';
	};

	const currentMessageId = getCurrentMessageId();
	const originalUserQuestion = getOriginalUserQuestion();

	const chartDataRef = useRef(chartData);
	useEffect(() => {
		chartDataRef.current = chartData;
	}, [chartData]);

	const handleChartExport = useCallback(() => {
		const currentChartData = chartDataRef.current;

		if (currentChartData && (currentChartData.categories || currentChartData.series)) {
			const exportData = {
				categories: currentChartData.categories || [],
				series: currentChartData.series || [],
				title: currentChartData.title || 'Chart Data',
				xAxisTitle: currentChartData.xAxisTitle || 'Category',
				yAxisTitle: currentChartData.yAxisTitle || 'Value'
			};

			downloadChartAsCSV(exportData);
		} else if (onExportExcel) {
			onExportExcel();
		}
	}, [onExportExcel]);

	const handleTableExport = useCallback(() => {
		const currentChartData = chartDataRef.current;

		// Try to export directly from chartData if available (for streaming data)
		if (currentChartData && currentChartData.headers && currentChartData.rows) {
			try {
				// Dynamic import of XLSX to avoid bundle size issues
				import('xlsx').then((XLSX) => {
					const now = new Date();
					const dateTime = now.toISOString()
						.replace(/:/g, '-')
						.replace(/\..+/, '')
						.replace('T', '_');

					const fileName = `Table_Data_${dateTime}.xlsx`;

					const worksheet = XLSX.utils.aoa_to_sheet([currentChartData.headers, ...currentChartData.rows]);
					const workbook = XLSX.utils.book_new();
					XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
					XLSX.writeFile(workbook, fileName);
				}).catch(() => {
					// Fallback to onExportExcel if XLSX import fails
					if (onExportExcel) {
						onExportExcel();
					}
				});
			} catch {
				if (onExportExcel) {
					onExportExcel();
				}
			}
		} else if (onExportExcel) {
			onExportExcel();
		}
	}, [onExportExcel]);

	const exportHandler = useCallback(() => {
		if (chartType === 'table') {
			handleTableExport();
		} else {
			handleChartExport();
		}
	}, [chartType, handleTableExport, handleChartExport]);

	const handleChartFullView = () => {
		document.dispatchEvent(new CustomEvent('hideTooltips'));

		// Use ref for latest chartData (important for streaming scenarios)
		const currentChartData = chartDataRef.current || chartData;

		// Allow fullscreen even without vizKey (for converted charts)
		if (onChartFullViewOpen && currentChartData && chartType) {
			onChartFullViewOpen({
				data: currentChartData,
				type: chartType,
				sqlQuery: sqlQuery,
				messageId: messageId,
				metadata: chartMetadata || {},
				filterData: filterData,
				customerId: customerId,
			});
		} else {
			if (onFullView) onFullView();
		}
	};

	const handleTableFullView = () => {
		document.dispatchEvent(new CustomEvent('hideTooltips'));

		// Use ref for latest chartData (important for streaming scenarios)
		const currentChartData = chartDataRef.current || chartData;

		// Try the new callback first (for streaming scenarios)
		if (onTableFullViewOpen && currentChartData) {
			onTableFullViewOpen({
				data: currentChartData,
				sqlQuery: sqlQuery,
				messageId: messageId,
				filterData: filterData,
				customerId: customerId,
				llmPayload: llmPayload,
			});
		} else if (onTableFullView) {
			// Fallback to ref-based approach for non-streaming
			onTableFullView();
		}
	};

	const handleAddFilterClick = async () => {
		if (onFilterDrawerOpen) {
			const messageIdToUse = messageId || currentMessageId;
			let columnsToUse: string[] = [];
			let filterDataMapToUse = filterDataMap;

			if (sqlQuery) {
				columnsToUse = extractColumnsFromSQL(sqlQuery);
			}

			onFilterDrawerOpen({
				availableColumns: columnsToUse,
				filterDataMap: filterDataMapToUse,
				existingFilters: convertAppliedFiltersToFilterRows(),
				onApply: handleApplyFiltersFromDrawer,
				isLoading: true,
			});

			if (!messageIdToUse) {
				console.error('No message ID available');
				return;
			}

			setLoadingColumns(true);

			try {
				const response = await aiChatApiService.getAvailableFilterColumns(messageIdToUse);

				const columnNames = Object.keys(response.filter_columns);
				const newFilterDataMap: Record<string, FilterColumnData> = {};
				columnNames.forEach((columnName) => {
					const columnData = response.filter_columns[columnName];
					newFilterDataMap[columnName.toLowerCase()] = {
						field_name: columnName,
						data_type: columnData.data_type,
						suggestion: columnData.suggestion,
						values: columnData.values.filter((v): v is string => v !== null),
						operators: columnData.operators,
					};
				});

				const updatedFilterDataMap = {
					...filterDataMap,
					...newFilterDataMap,
				};

				setFilterDataMap(updatedFilterDataMap);
				setLoadingColumns(false);

				onFilterDrawerOpen({
					availableColumns: columnNames,
					filterDataMap: updatedFilterDataMap,
					existingFilters: convertAppliedFiltersToFilterRows(),
					onApply: handleApplyFiltersFromDrawer,
					isLoading: isApplyingFilters || false,
				});
			} catch (error) {
				console.error('Error fetching available columns:', error);
				setLoadingColumns(false);

				onFilterDrawerOpen({
					availableColumns: columnsToUse,
					filterDataMap: filterDataMapToUse,
					existingFilters: convertAppliedFiltersToFilterRows(),
					onApply: handleApplyFiltersFromDrawer,
					isLoading: isApplyingFilters || false,
				});
			}
			return;
		}

		setIsFilterDrawerOpen(true);
		setLoadingColumns(true);

		try {
			const messageIdToUse = messageId || currentMessageId;
			if (!messageIdToUse) {
				console.error('No message ID available');
				setLoadingColumns(false);
				return;
			}

			const response = await aiChatApiService.getAvailableFilterColumns(messageIdToUse);

			const columnNames = Object.keys(response.filter_columns);
			setAvailableColumns(columnNames);

			const newFilterDataMap: Record<string, FilterColumnData> = {};
			columnNames.forEach((columnName) => {
				const columnData = response.filter_columns[columnName];
				newFilterDataMap[columnName.toLowerCase()] = {
					field_name: columnName,
					data_type: columnData.data_type,
					suggestion: columnData.suggestion,
					values: columnData.values.filter((v): v is string => v !== null),
					operators: columnData.operators,
				};
			});

			setFilterDataMap((prev) => ({
				...prev,
				...newFilterDataMap,
			}));

			setLoadingColumns(false);
		} catch (error) {
			console.error('Error fetching available columns:', error);
			if (sqlQuery) {
				const columns = extractColumnsFromSQL(sqlQuery);
				setAvailableColumns(columns);
			}
			setLoadingColumns(false);
		}
	};

	const extractColumnsFromSQL = (query: string): string[] => {
		const selectMatch = query.match(/SELECT\s+(.*?)\s+FROM/i);
		if (!selectMatch) return [];

		const selectClause = selectMatch[1];
		const columns = selectClause
			.split(',')
			.map(col => {
				const aliasMatch = col.trim().match(/\s+AS\s+(\w+)/i);
				if (aliasMatch) return aliasMatch[1];

				const funcMatch = col.trim().match(/\w+\((.+?)\)/);
				if (funcMatch) {
					const inner = funcMatch[1].trim();
					return inner.split('.').pop()?.replace(/['"]/g, '') || '';
				}

				const cleanCol = col.trim().split('.').pop()?.replace(/['"]/g, '') || '';
				return cleanCol;
			})
			.filter(col => col && col !== '*')
			.map(col => col.toLowerCase());

		return [...new Set(columns)];
	};

	const convertAppliedFiltersToFilterRows = () => {
		return appliedFilters.map((filter, index) => {
			const isArray = Array.isArray(filter.value);
			let selectedValues: string[] = [];
			let value = '';

			if (isArray) {
				selectedValues = filter.value as string[];
				value = '';
			} else {
				const stringValue = filter.value as string;
				// Check if it's a comma-separated string (likely from dropdown filters)
				if (stringValue && stringValue.includes(',')) {
					selectedValues = stringValue.split(',').map(v => v.trim()).filter(v => v);
					value = '';
				} else {
					// Check if this column is a dropdown type
					const columnData = filterDataMap[filter.field?.toLowerCase()];
					const isDropdownColumn = columnData?.suggestion?.toLowerCase() === 'dropdown';

					if (isDropdownColumn && stringValue) {
						// For dropdown columns, put single values in selectedValues
						selectedValues = [stringValue];
						value = '';
					} else {
						value = stringValue;
						selectedValues = [];
					}
				}
			}

			const columnName = (filter.originalField || filter.field).split('::')[0].split('.').pop() || filter.field;

			return {
				id: `existing_${filter.field}_${filter.operator}_${index}`,
				column: columnName,
				operator: filter.operator,
				value: value,
				selectedValues: selectedValues,
				isExisting: true,
			};
		});
	};

	const handleApplyFiltersFromDrawer = async (filters: any[]) => {
		try {
			setIsApplyingFilters(true);
			if (onInsightsLoadingChange) {
				onInsightsLoadingChange(true);
			}

			const messageIdToUse = messageId || currentMessageId;
			if (!messageIdToUse) {
				throw new Error('No message ID available for filter update');
			}

			const requestFilterData: FilterItem[] = [];
			const newFields: any[] = [];

			filters.forEach((filter) => {
				if (filter.isDeleted) {
					return;
				}

				const columnData = filterDataMap[filter.column.toLowerCase()];
				const filterType = columnData?.suggestion?.toLowerCase() || 'entertext';
				const isDropdown = filterType === 'dropdown';
				const columnValue = isDropdown
					? filter.selectedValues.join(',')
					: filter.value;

				if (columnValue.trim() === '') return;

				const filterItem: FilterItem = {
					operator: filter.operator,
					columnName: filter.column,
					columnValue: columnValue,
					filterType: filterType,
					baseOperator: filter.operator,
					isDeleted: false,
				};

				requestFilterData.push(filterItem);
			});

			const filterRequest: any = {
				prompt: originalUserQuestion,
				message_id: messageIdToUse,
				filter_data: requestFilterData,
			};



			setIsFilterDrawerOpen(false);

			let accumulatedData: any = {
				data: null,
				sql_query: '',
				metadata: null,
				visualization: null,
				insights: {
					summary_insights: undefined,
					dimension_insights: undefined,
					time_insights: undefined,
					report_insights: undefined
				}
			};

			await aiChatApiService.sendFilterQueryStream(
				filterRequest,
				(chunk: any) => {
					if (typeof chunk === 'string') {
						try {
							const parsedChunk = JSON.parse(chunk);
							handleStreamingChunk(parsedChunk, accumulatedData);
						} catch (e) {
						}
					} else if (typeof chunk === 'object') {
						handleStreamingChunk(chunk, accumulatedData);

						if (onFilterUpdateComplete) {
							if (chunk.data && chunk.visualization && chunk.sql_query) {
								onFilterUpdateComplete({
									...accumulatedData,
									insights: { ...accumulatedData.insights }
								});
							}
							else if (chunk.type === 'insight') {
								onFilterUpdateComplete({
									...accumulatedData,
									insights: { ...accumulatedData.insights }
								});
							}
							else if (chunk.type === 'insights_complete') {
								if (onInsightsLoadingChange) {
									onInsightsLoadingChange(false);
								}
							}
						}
					}
				},
				(error: Error) => {
					console.error('Error in filter query streaming:', error);
					setIsApplyingFilters(false);
					if (onInsightsLoadingChange) {
						onInsightsLoadingChange(false);
					}
				},
				() => {
					if (onFilterUpdateComplete) {
						onFilterUpdateComplete({
							...accumulatedData,
							insights: { ...accumulatedData.insights }
						});
					}
					setIsApplyingFilters(false);
					if (onInsightsLoadingChange) {
						onInsightsLoadingChange(false);
					}
				}
			);
		} catch (error) {
			console.error('Error applying filters:', error);
			setIsApplyingFilters(false);
			if (onInsightsLoadingChange) {
				onInsightsLoadingChange(false);
			}
		}
	};


	const formatFieldName = (fieldName: string): string => {
		return fieldName
			.toLowerCase()
			.split('_')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	};

	const renderDisplayName = (displayName: string) => {
		const parts = displayName.split(/\*\*/);
		return parts.map((part, index) => {
			if (index % 2 === 1) {
				return (
					<strong key={index} className="font-semibold">
						{part}
					</strong>
				);
			}
			return part;
		});
	};

	const handleFilterClick = async (filter: AppliedFilter, index: number) => {
		const filterId = `${filter.field}_${filter.operator}_${index}`;

		if (activeDropdown === filterId) {
			setActiveDropdown(null);
			return;
		}

		setActiveDropdown(filterId);

		// If filter data already exists (e.g., from metadata), don't fetch from API
		if (filterDataMap[filter.field]) {
			return;
		}

		const allFields = [...new Set(appliedFilters.map((f) => f.field))];

		const fieldsNeedingData = allFields.filter((field) => !filterDataMap[field]);

		if (fieldsNeedingData.length > 0) {
			setLoadingFilters((prev) => ({
				...prev,
				[filter.field]: true,
			}));
			try {
				const allFilterData = await aiChatApiService.GetSqlFiltersForMultipleFields(allFields, customerId);

				const newFilterDataMap: Record<string, FilterColumnData> = {};
				allFilterData.forEach((filterData) => {
					if (filterData.field_name) {
						newFilterDataMap[filterData.field_name.toLowerCase()] = filterData;
					} else {
						const fieldIndex = allFilterData.indexOf(filterData);
						if (fieldIndex < allFields.length) {
							newFilterDataMap[allFields[fieldIndex].toLowerCase()] = filterData;
						}
					}
				});

				setFilterDataMap((prev) => ({
					...prev,
					...newFilterDataMap,
				}));
			} catch (error) {
				console.error('Error fetching filter values:', error);
			} finally {
				setLoadingFilters((prev) => ({
					...prev,
					[filter.field]: false,
				}));
			}
		}
	};

	const handleValueSelect = async (filter: AppliedFilter, value: string, index: number) => {
		const filterId = `${filter.field}_${filter.operator}_${index}`;

		setUpdatingFilter(filterId);

		const currentValue = Array.isArray(filter.value) ? filter.value.join(',') : filter.value;
		if (value === currentValue) {
			setUpdatingFilter(null);
			return;
		}

		if (onFilterValueChange) {
			onFilterValueChange(filter, value);
		}

		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
		}

		debounceTimeoutRef.current = setTimeout(async () => {
			await makeApiCall(filter, value);
		}, 500);
	};

	const makeApiCall = async (filter: AppliedFilter, value: string) => {
		try {
			if (onInsightsLoadingChange) {
				onInsightsLoadingChange(true);
			}

			if (!currentMessageId) {
				throw new Error('No message ID available for filter update');
			}

			const allFilters = parseFiltersFromSQL(sqlQuery || '');
			const requestFilterData: FilterItem[] = [];

			allFilters.forEach((f) => {
				const currentValue = Array.isArray(f.value) ? f.value.join(',') : f.value;
				const apiFilterData = filterDataMap[f.field.toLowerCase()];
				const filterType = apiFilterData?.suggestion?.toLowerCase() || f.filterType || 'entertext';
				const baseOperator = f.baseOperator || f.operator;

				const columnName = f.originalField || f.field;

				if (f.field === filter.field && f.operator === filter.operator) {
					if (value.trim() !== '') {
						requestFilterData.push({
							operator: f.operator,
							columnName: columnName,
							columnValue: value,
							filterType: filterType,
							baseOperator: baseOperator,
							isDeleted: false,
						});
					}
				} else {
					if (currentValue.trim() !== '') {
						requestFilterData.push({
							operator: f.operator,
							columnName: columnName,
							columnValue: currentValue,
							filterType: filterType,
							baseOperator: baseOperator,
							isDeleted: false,
						});
					}
				}
			});

			const filterRequest = {
				prompt: originalUserQuestion,
				message_id: currentMessageId,
				filter_data: requestFilterData,
			};

			let accumulatedData: any = {
				data: null,
				sql_query: '',
				metadata: null,
				visualization: null,
				insights: {
					summary_insights: undefined,
					dimension_insights: undefined,
					time_insights: undefined,
					report_insights: undefined
				}
			};

			await aiChatApiService.sendFilterQueryStream(
				filterRequest,
				(chunk: any) => {
					if (typeof chunk === 'string') {
						try {
							const parsedChunk = JSON.parse(chunk);
							handleStreamingChunk(parsedChunk, accumulatedData);
						} catch (e) {
						}
					} else if (typeof chunk === 'object') {
						handleStreamingChunk(chunk, accumulatedData);

						if (onFilterUpdateComplete) {
							if (chunk.data && chunk.visualization && chunk.sql_query) {
								onFilterUpdateComplete({
									...accumulatedData,
									insights: { ...accumulatedData.insights }
								});
							}
							else if (chunk.type === 'insight') {
								onFilterUpdateComplete({
									...accumulatedData,
									insights: { ...accumulatedData.insights }
								});
							}
							else if (chunk.type === 'insights_complete') {
								if (onInsightsLoadingChange) {
									onInsightsLoadingChange(false);
								}
							}
						}
					}
				},
				(error: Error) => {
					console.error('Error in filter query streaming:', error);
					if (onInsightsLoadingChange) {
						onInsightsLoadingChange(false);
					}
				},
				() => {
					if (onFilterUpdateComplete) {
						onFilterUpdateComplete({
							...accumulatedData,
							insights: { ...accumulatedData.insights }
						});
					}
					if (onInsightsLoadingChange) {
						onInsightsLoadingChange(false);
					}
				}
			);
		} catch (error) {
			console.error('Error updating filter:', error);
			if (onInsightsLoadingChange) {
				onInsightsLoadingChange(false);
			}
		} finally {
			setUpdatingFilter(null);
		}
	};

	const handleStreamingChunk = (chunk: any, accumulatedData: any) => {
		if (chunk.type === 'insight') {
			if (chunk.insight_type === 'report_insights') {
				let reportData = null;
				if (chunk.result?.report_data && chunk.result.report_data.length > 0) {
					reportData = chunk.result.report_data;
				} else if (chunk.report_data && chunk.report_data.length > 0) {
					reportData = chunk.report_data;
				} else if (chunk.data && Array.isArray(chunk.data)) {
					reportData = chunk.data;
				}

				if (reportData) {
					accumulatedData.insights.report_insights = reportData;
				}
			} else if (chunk.insight_type && chunk.result?.llm_insights?.insights) {
				switch (chunk.insight_type) {
					case 'summary_insights':
						accumulatedData.insights.summary_insights = chunk.result.llm_insights.insights;
						break;
					case 'dimension_insights':
						accumulatedData.insights.dimension_insights = chunk.result.llm_insights.insights;
						break;
					case 'time_insights':
						accumulatedData.insights.time_insights = chunk.result.llm_insights.insights;
						break;
				}
			}
		} else if (chunk.data && chunk.visualization && chunk.sql_query && !chunk.type) {
			accumulatedData.data = chunk.data;
			accumulatedData.sql_query = chunk.sql_query;
			accumulatedData.metadata = chunk.metadata;
			accumulatedData.visualization = chunk.visualization;
		} else if (chunk.data && chunk.visualization && chunk.sql_query) {
			accumulatedData.data = chunk.data;
			accumulatedData.sql_query = chunk.sql_query;
			accumulatedData.metadata = chunk.metadata;
			accumulatedData.visualization = chunk.visualization;
		}
	};

	const handleClickOutside = (event: MouseEvent) => {
		const target = event.target as Element;
		if (!target.closest('.filter-dropdown-container')) {
			const activeFilter = appliedFilters.find((_, index) => {
				const filterId = `${appliedFilters[index].field}_${appliedFilters[index].operator}_${index}`;
				return activeDropdown === filterId;
			});

			if (activeFilter) {
				const filterData = filterDataMap[activeFilter.field];
				const isMultiSelect = filterData?.suggestion?.toLowerCase() === 'dropdown';

				if (!isMultiSelect) {
					setActiveDropdown(null);
				}
			} else {
				setActiveDropdown(null);
			}
		}
	};

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);


	const parseFiltersFromSQL = (query: string): AppliedFilter[] => {
		if (!query) return [];

		const filters: AppliedFilter[] = [];
		const cleanQuery = query.replace(/\s+/g, ' ').trim();

		let allWhereClauses: string[] = [];

		if (cleanQuery.toUpperCase().startsWith('WITH')) {
			const lastSelectIndex = cleanQuery.lastIndexOf('SELECT');
			if (lastSelectIndex !== -1) {
				const finalSelectQuery = cleanQuery.substring(lastSelectIndex);
				const finalWhereMatch = finalSelectQuery.match(/WHERE\s+(.*?)(?:\s+GROUP\s+BY|\s+ORDER\s+BY|\s+FROM|\s+LEFT\s+JOIN|\s+INNER\s+JOIN|\s+RIGHT\s+JOIN|;|$)/i);

				if (finalWhereMatch) {
					allWhereClauses.push(finalWhereMatch[1].trim());
				}

				const extractFirstCTEWhere = (sql: string): string | null => {
					const withMatch = sql.match(/WITH\s+(\w+)\s+AS\s*\(/i);
					if (!withMatch) return null;

					const cteStart = withMatch.index! + withMatch[0].length;
					let depth = 1;
					let pos = cteStart;

					while (pos < sql.length && depth > 0) {
						if (sql[pos] === '(') depth++;
						else if (sql[pos] === ')') depth--;
						pos++;
					}

					if (depth === 0) {
						const cteBody = sql.substring(cteStart, pos - 1);
						const whereMatch = cteBody.match(/WHERE\s+(.*?)(?:\s+GROUP\s+BY|\s+ORDER\s+BY|\s+FROM|\s+LEFT\s+JOIN|\s+INNER\s+JOIN|\s+RIGHT\s+JOIN|\)|$)/i);
						if (whereMatch) {
							return whereMatch[1].trim();
						}
					}

					return null;
				};

				const firstCTEWhere = extractFirstCTEWhere(cleanQuery);
				if (firstCTEWhere) {
					allWhereClauses.push(firstCTEWhere);
				}
			}
		} else {
			const whereMatch = cleanQuery.match(/WHERE\s+(.*?)(?:\s+GROUP\s+BY|\s+ORDER\s+BY|\s+FROM|\s+LEFT\s+JOIN|\s+INNER\s+JOIN|\s+RIGHT\s+JOIN|;|$)/i);
			if (whereMatch) {
				allWhereClauses.push(whereMatch[1].trim());
			}
		}

		if (allWhereClauses.length === 0) return filters;

		for (const whereClause of allWhereClauses) {
			parseConditions(whereClause, filters);
		}

		return deduplicateFilters(filters);
	};

	const deduplicateFilters = (filters: AppliedFilter[]): AppliedFilter[] => {
		const seen = new Map<string, AppliedFilter>();

		for (const filter of filters) {
			const key = `${filter.field.toLowerCase()}_${filter.operator}_${Array.isArray(filter.value) ? filter.value.sort().join(',') : filter.value}`;

			if (!seen.has(key)) {
				seen.set(key, filter);
			} else {
				const existing = seen.get(key)!;
				if (Array.isArray(filter.value) && Array.isArray(existing.value)) {
					const filterSet = new Set(filter.value);
					const existingSet = new Set(existing.value);
					if (filterSet.size > existingSet.size) {
						seen.set(key, filter);
					}
				}
			}
		}

		return Array.from(seen.values());
	};

	const parseConditions = (whereClause: string, filters: AppliedFilter[]) => {
		const operatorMappings: Record<string, string> = {
			'=': 'equals',
			'!=': 'not equals',
			'<>': 'not equals',
			'>': 'greater than',
			'>=': 'greater than or equal to',
			'<': 'less than',
			'<=': 'less than or equal to',
			LIKE: 'contains',
			ILIKE: 'contains',
			IN: 'is one of',
			'NOT IN': 'is not one of',
		};

		const conditions = whereClause.split(/\s+AND\s+/i);

		conditions.forEach((condition) => {
			const trimmedCondition = condition.trim();

			const likeWithCastMatch = trimmedCondition.match(/(\w+(?:::\w+)?)\s+(I?LIKE)\s+'%([^%]+)%'/i);
			if (likeWithCastMatch) {
				const [, fieldWithCast, operator, value] = likeWithCastMatch;
				const field = fieldWithCast.split('::')[0];
				const displayName = formatFieldName(field);
				filters.push({
					field: field.toLowerCase(),
					operator: operator.toUpperCase(),
					value: value,
					displayName: `${displayName} contains **${value}**`,
					filterType: 'entertext',
					baseOperator: operator.toUpperCase(),
					originalField: fieldWithCast,
				});
				return;
			}

			const likeMatch = trimmedCondition.match(/(\w+)\s+(I?LIKE)\s+'%([^%]+)%'/i);
			if (likeMatch) {
				const [, field, operator, value] = likeMatch;
				const displayName = formatFieldName(field);
				filters.push({
					field: field.toLowerCase(),
					operator: operator.toUpperCase(),
					value: value,
					displayName: `${displayName} contains **${value}**`,
					filterType: 'entertext',
					baseOperator: operator.toUpperCase(),
				});
				return;
			}

			const comparisonWithCastMatch = trimmedCondition.match(
				/(\w+(?:::\w+)?)\s*(>=|<=|<>|!=|>|<|=)\s*'([^']+)'/i
			);
			if (comparisonWithCastMatch) {
				const [, fieldWithCast, operator, value] = comparisonWithCastMatch;
				const field = fieldWithCast.split('::')[0];
				const operatorText = operatorMappings[operator] || operator;
				const displayName = formatFieldName(field);
				filters.push({
					field: field.toLowerCase(),
					operator,
					value,
					displayName: `${displayName} ${operatorText} **${value}**`,
					filterType: 'entertext',
					baseOperator: operator,
					originalField: fieldWithCast,
				});
				return;
			}

			const comparisonMatch = trimmedCondition.match(/(\w+)\s*(>=|<=|<>|!=|>|<|=)\s*'([^']+)'/i);
			if (comparisonMatch) {
				const [, field, operator, value] = comparisonMatch;
				const operatorText = operatorMappings[operator] || operator;
				const displayName = formatFieldName(field);
				filters.push({
					field: field.toLowerCase(),
					operator,
					value,
					displayName: `${displayName} ${operatorText} **${value}**`,
					filterType: 'entertext',
					baseOperator: operator,
				});
				return;
			}

			const numericWithCastMatch = trimmedCondition.match(
				/(\w+(?:::\w+)?)\s*(>=|<=|<>|!=|>|<|=)\s*(\d+(?:\.\d+)?)/i
			);
			if (numericWithCastMatch) {
				const [, fieldWithCast, operator, value] = numericWithCastMatch;
				const field = fieldWithCast.split('::')[0];
				const operatorText = operatorMappings[operator] || operator;
				const displayName = formatFieldName(field);
				filters.push({
					field: field.toLowerCase(),
					operator,
					value,
					displayName: `${displayName} ${operatorText} **${value}**`,
					filterType: 'enternumber',
					baseOperator: operator,
					originalField: fieldWithCast,
				});
				return;
			}

			const numericMatch = trimmedCondition.match(/(\w+)\s*(>=|<=|<>|!=|>|<|=)\s*(\d+(?:\.\d+)?)/i);
			if (numericMatch) {
				const [, field, operator, value] = numericMatch;
				const operatorText = operatorMappings[operator] || operator;
				const displayName = formatFieldName(field);
				filters.push({
					field: field.toLowerCase(),
					operator,
					value,
					displayName: `${displayName} ${operatorText} **${value}**`,
					filterType: 'enternumber',
					baseOperator: operator,
				});
				return;
			}

			const dateWithCastMatch = trimmedCondition.match(
				/(\w+(?:::\w+)?)\s*(>=|<=|<>|!=|>|<|=)\s*'(\d{4}-\d{2}-\d{2})'/i
			);
			if (dateWithCastMatch) {
				const [, fieldWithCast, operator, value] = dateWithCastMatch;
				const field = fieldWithCast.split('::')[0];
				const operatorText = operatorMappings[operator] || operator;
				const displayName = formatFieldName(field);
				filters.push({
					field: field.toLowerCase(),
					operator,
					value,
					displayName: `${displayName} ${operatorText} ${value}`,
					filterType: 'datepicker',
					baseOperator: operator,
					originalField: fieldWithCast,
				});
				return;
			}

			const dateMatch = trimmedCondition.match(/(\w+)\s*(>=|<=|<>|!=|>|<|=)\s*'(\d{4}-\d{2}-\d{2})'/i);
			if (dateMatch) {
				const [, field, operator, value] = dateMatch;
				const operatorText = operatorMappings[operator] || operator;
				const displayName = formatFieldName(field);
				filters.push({
					field: field.toLowerCase(),
					operator,
					value,
					displayName: `${displayName} ${operatorText} ${value}`,
					filterType: 'datepicker',
					baseOperator: operator,
				});
				return;
			}

			const inWithCastMatch = trimmedCondition.match(/(\w+(?:\.\w+)?(?:::\w+)?)\s+(IN|NOT IN)\s*\(([^)]+)\)/i);
			if (inWithCastMatch) {
				const [, fieldWithCast, operator, valuesString] = inWithCastMatch;
				const field = fieldWithCast.split('::')[0].split('.')[1] || fieldWithCast.split('::')[0];
				const values = valuesString.split(',').map((v) => v.trim().replace(/['"]/g, ''));
				const operatorText = operatorMappings[operator] || operator;
				const displayName = formatFieldName(field);
				filters.push({
					field: field.toLowerCase(),
					operator: operator.toUpperCase(),
					value: values,
					displayName: `${displayName} ${operatorText} (**${values.join('**, **')}**)`,
					filterType: 'dropdown',
					baseOperator: operator.toUpperCase(),
					originalField: fieldWithCast,
				});
				return;
			}

			const inMatch = trimmedCondition.match(/(\w+(?:\.\w+)?)\s+(IN|NOT IN)\s*\(([^)]+)\)/i);
			if (inMatch) {
				const [, field, operator, valuesString] = inMatch;
				const cleanField = field.split('.')[1] || field;
				const values = valuesString.split(',').map((v) => v.trim().replace(/['"]/g, ''));
				const operatorText = operatorMappings[operator] || operator;
				const displayName = formatFieldName(cleanField);
				filters.push({
					field: cleanField.toLowerCase(),
					operator: operator.toUpperCase(),
					value: values,
					displayName: `${displayName} ${operatorText} (**${values.join('**, **')}**)`,
					filterType: 'dropdown',
					baseOperator: operator.toUpperCase(),
				});
				return;
			}

			const ilikeWithCastMatch = trimmedCondition.match(/(\w+(?:::\w+)?)\s+(I?LIKE)\s+'([^']+)'/i);
			if (ilikeWithCastMatch) {
				const [, fieldWithCast, operator, value] = ilikeWithCastMatch;
				const field = fieldWithCast.split('::')[0];
				const operatorText = operatorMappings[operator] || operator;
				const displayName = formatFieldName(field);
				filters.push({
					field: field.toLowerCase(),
					operator: operator.toUpperCase(),
					value: value,
					displayName: `${displayName} ${operatorText} **${value}**`,
					baseOperator: operator.toUpperCase(),
					originalField: fieldWithCast,
				});
				return;
			}

			const ilikeMatch = trimmedCondition.match(/(\w+)\s+(I?LIKE)\s+'([^']+)'/i);
			if (ilikeMatch) {
				const [, field, operator, value] = ilikeMatch;
				const operatorText = operatorMappings[operator] || operator;
				const displayName = formatFieldName(field);
				filters.push({
					field: field.toLowerCase(),
					operator: operator.toUpperCase(),
					value: value,
					displayName: `${displayName} ${operatorText} **${value}**`,
					baseOperator: operator.toUpperCase(),
				});
				return;
			}

			const genericWithCastMatch = trimmedCondition.match(/(\w+(?:::\w+)?)\s*(.*)/);
			if (genericWithCastMatch) {
				const [, fieldWithCast, rest] = genericWithCastMatch;
				const field = fieldWithCast.split('::')[0];
				const displayName = formatFieldName(field);
				filters.push({
					field: field.toLowerCase(),
					operator: 'custom',
					value: rest.trim(),
					displayName: `${displayName}: ${rest.trim()}`,
					filterType: 'entertext',
					baseOperator: 'custom',
					originalField: fieldWithCast,
				});
				return;
			}

			const genericMatch = trimmedCondition.match(/(\w+)\s*(.*)/);
			if (genericMatch) {
				const [, field, rest] = genericMatch;
				const displayName = formatFieldName(field);
				filters.push({
					field: field.toLowerCase(),
					operator: 'custom',
					value: rest.trim(),
					displayName: `${displayName}: ${rest.trim()}`,
					filterType: 'entertext',
					baseOperator: 'custom',
				});
			}
		});

		return filters;
	};

	const appliedFilters = React.useMemo(() => {
		// Get filters from metadata only (not using SQL parsing for now)
		const metadataFilters: AppliedFilter[] = [];

		if (chartMetadata?.timeframe_start_date && chartMetadata?.timeframe_end_date) {
			// Add start date filter
			metadataFilters.push({
				field: 'timeframe_start_date',
				operator: '>=',
				value: chartMetadata.timeframe_start_date,
				displayName: `Start date **${chartMetadata.timeframe_start_date}**`,
				filterType: 'datepicker',
				baseOperator: '>=',
			});

			// Add end date filter
			metadataFilters.push({
				field: 'timeframe_end_date',
				operator: '<=',
				value: chartMetadata.timeframe_end_date,
				displayName: `End date **${chartMetadata.timeframe_end_date}**`,
				filterType: 'datepicker',
				baseOperator: '<=',
			});
		}

		// Note: SQL parsing code is kept below but not used here
		// const sqlFilters = parseFiltersFromSQL(sqlQuery || '');

		return metadataFilters;
	}, [chartMetadata]);

	const [, setCurrentFilterData] = useState<FilterItem[]>([]);

	const copyMessageContent = () => {
		if (!message) return;

		let contentToCopy = '';

		if (message.content) {
			contentToCopy += message.content + '\n\n';
		}

		if (message.insights) {
			if (message.insights.summary_insights) {
				contentToCopy += message.insights.summary_insights + '\n\n';
			}

			if (message.insights.dimension_insights) {
				contentToCopy += message.insights.dimension_insights + '\n\n';
			}

			if (message.insights.time_insights) {
				contentToCopy += message.insights.time_insights;
			}
		}

		const htmlContent = contentToCopy.trim();

		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = htmlContent;

		const clipboardData = new ClipboardItem({
			'text/html': new Blob([htmlContent], { type: 'text/html' }),
			'text/plain': new Blob([tempDiv.textContent || ''], { type: 'text/plain' }),
		});

		navigator.clipboard
			.write([clipboardData])
			.then(() => {
				setIsCopied(true);
				setTimeout(() => {
					setIsCopied(false);
				}, 2000);
			})
			.catch((err) => {
				navigator.clipboard
					.writeText(tempDiv.textContent || '')
					.then(() => {
						setIsCopied(true);
						setTimeout(() => {
							setIsCopied(false);
						}, 2000);
					})
					.catch((fallbackErr) => {
						console.error('Failed to copy content: ', fallbackErr);
					});
			});
	};


	const formatSQLQuery = (sql: string) => {
		try {
			const formattedSQL = format(sql, {
				language: 'sql',
				tabWidth: 2,
				useTabs: false,
				keywordCase: 'upper',
				linesBetweenQueries: 1,
			});

			return (
				<SyntaxHighlighter
					language="sql"
					style={coy}
					customStyle={{
						margin: 0,
						fontSize: '0.75rem',
						lineHeight: '1.4',
						borderRadius: '0.375rem',
						backgroundColor: 'transparent',
					}}
					codeTagProps={{
						style: { padding: 0 },
					}}
					showLineNumbers={false}
					wrapLines={true}
				>
					{formattedSQL}
				</SyntaxHighlighter>
			);
		} catch (error) {
			return <span className="text-gray-800">{sql}</span>;
		}
	};

	const createFilterData = React.useCallback((): FilterItem[] => {
		const filterData: FilterItem[] = [];
		appliedFilters.forEach((f) => {
			const currentValue = Array.isArray(f.value) ? f.value.join(',') : f.value;
			const apiFilterData = filterDataMap[f.field.toLowerCase()];
			const filterType = apiFilterData?.suggestion?.toLowerCase() || f.filterType || 'entertext';
			const baseOperator = f.baseOperator || f.operator;

			const columnName = f.originalField || f.field;

			filterData.push({
				operator: f.operator,
				columnName: columnName,
				columnValue: currentValue,
				filterType: filterType,
				baseOperator: baseOperator,
				isDeleted: false,
			});
		});
		return filterData;
	}, [appliedFilters, filterDataMap]);

	const onFilterDataChangeRef = useRef(onFilterDataChange);
	onFilterDataChangeRef.current = onFilterDataChange;

	const prevFilterDataRef = useRef<string>('');

	useEffect(() => {
		if (appliedFilters.length > 0) {
			const filterData = createFilterData();
			const filterDataString = JSON.stringify(filterData);

			if (prevFilterDataRef.current !== filterDataString) {
				setCurrentFilterData(filterData);
				prevFilterDataRef.current = filterDataString;

				if (onFilterDataChangeRef.current) {
					onFilterDataChangeRef.current(filterData);
				}
			}
		} else {
			setCurrentFilterData([]);
			prevFilterDataRef.current = '';
			if (onFilterDataChangeRef.current) {
				onFilterDataChangeRef.current([]);
			}
		}
	}, [createFilterData]);

	const filtersToShow = showAllFilters ? appliedFilters : appliedFilters.slice(0, MAX_VISIBLE_FILTERS);
	const hiddenFiltersCount = appliedFilters.length - MAX_VISIBLE_FILTERS;
	const shouldShowSeeMore = appliedFilters.length > MAX_VISIBLE_FILTERS;

	return (
		<div className="w-full">
			<div className={`flex items-center  pb-2 justify-between mb-2`}>
				{appliedFilters.length > 0 && (
					<div className="flex flex-wrap">
						{filtersToShow.map((filter, index) => {
							const filterId = `${filter.field}_${filter.operator}_${index}`;
							const isActive = activeDropdown === filterId;
							const isUpdating = updatingFilter === filterId;
							const filterData = filterDataMap[filter.field];
							const isLoading = loadingFilters[filter.field] || false;
							const isMetadataDateFilter = filter.field === 'timeframe_start_date' || filter.field === 'timeframe_end_date';

							return (
								<div key={index} className="filter-dropdown-container relative">
									<div
										className={`flex items-center space-x-2 bg-white p-2 transition-colors ${isMetadataDateFilter
											? 'cursor-default'
											: isUpdating
												? 'opacity-75 cursor-not-allowed'
												: 'cursor-pointer hover:bg-blue-50'
											}`}
										onClick={() => !isMetadataDateFilter && !isUpdating && handleFilterClick(filter, index)}
										data-filter-container={filterId}
									>
										<span className="text-ui text-tertiary-text">{renderDisplayName(filter.displayName)}</span>
										{!isMetadataDateFilter && (
											<>
												{isUpdating ? (
													<FaSpinner className="w-3 h-3 text-primary animate-spin" />
												) : isLoading && !filterData ? (
													<FaSpinner className="w-3 h-3 text-primary animate-spin" />
												) : (
													<FaChevronDown
														className={`w-3 h-3 text-tertiary-text transition-transform ${isActive ? 'rotate-180' : ''}`}
													/>
												)}
											</>
										)}
									</div>

									{!isMetadataDateFilter && isActive && filterData && (
										<div className="absolute top-full mt-1 w-[300px]">
											<DynamicFilterInput
												columnName={filter.field}
												filterData={filterData}
												currentValue={
													Array.isArray(filter.value) ? filter.value.join(',') : filter.value
												}
												onValueChange={(value: string) => handleValueSelect(filter, value, index)}
												placeholder={`Enter ${filter.field.toLowerCase()}`}
												onCloseDropdown={() => setActiveDropdown(null)}
											/>
										</div>
									)}
								</div>
							);
						})}


						{shouldShowSeeMore && (
							<div className="flex items-center">
								<button
									onClick={() => setShowAllFilters(!showAllFilters)}
									className="flex items-center space-x-1 text-tertiary-text hover:bg-[#e6e6e6] text-ui transition-colors duration-200 px-2 py-1 rounded-full"
								>
									{showAllFilters ? (
										<>
											<span>See less</span>
											<FaChevronUp className="w-3 h-3" />
										</>
									) : (
										<>
											<span>See more ({hiddenFiltersCount})</span>
											<FaChevronDown className="w-3 h-3" />
										</>
									)}
								</button>
							</div>
						)}
					</div>
				)}


				{/* {
					chartData.title && chartData.title.trim() !== '' && (
							
					<div className="flex items-center space-x-2">
						<h4 
							className="text-ui"
							style={{
								fontFamily: 'Inter',
								fontWeight: 400,
								fontStyle: 'normal',
								fontSize: '14px',
								lineHeight: '28px',
								letterSpacing: '0px',
								verticalAlign: 'middle',
								color: '#242424'
							}}
						>
							{chartData.title}
						</h4>
					</div>
		
					)
				}  */}
				{/* <div className="flex items-center space-x-2">
					<h4 className="text-light-grey text-ui">Applied Filters</h4>
					<button
						onClick={handleAddFilterClick}
						className="p-2 group flex items-center justify-center rounded-full transition-colors active:text-primary text-primary-text hover:bg-secondary-background hover:text-primary"
						title="Edit filters"
					>
						<FiEdit2 className="w-4 h-4" strokeWidth={1.75} />
					</button>
				</div> */}
				<div className={`flex items-center space-x-1 top-3 ${hasChartSwitcher ? (chartType != 'linechart') ? '' : '' : ''}`}>
					{message && (
						<>
							{/* Unified ChartSwitcher - show when we have table source data (with table option) or for regular chart views */}
							{hasTableSourceData() && llmPayload?.columns && getDimensionColumns(llmPayload.columns).length > 0 && onChartTypeChangeWithData && (
								<ChartSwitcher
									defaultType="table"
									currentType={selectedChartType !== 'table' ? selectedChartType : 'table'}
									showTableOption={true}
									onTypeChange={(newType) => {
										if (newType === 'table') {
											setSelectedChartType('table');
											if (onChartTypeChangeWithData) {
												const sourceTableData = getSourceTableData();
												if (sourceTableData) {
													onChartTypeChangeWithData('table', { headers: sourceTableData.headers, rows: sourceTableData.rows });
												}
											} else if (onChartTypeChange) {
												onChartTypeChange('table');
											}
											return;
										}

										if (!llmPayload?.columns || llmPayload.columns.length === 0) {
											return;
										}

										const sourceTableData = getSourceTableData();
										if (!sourceTableData) {
											return;
										}

										setSelectedChartType(newType);
										const dimensionCol = selectedDimension || getDefaultDimensionColumn(llmPayload.columns) || '';
										const measureCols = selectedMeasures.length > 0 ? selectedMeasures : [getDefaultMeasureColumn(llmPayload.columns) || ''];

										if (dimensionCol && measureCols.length > 0 && measureCols[0]) {
											const transformedData = transformTableToChartData(
												sourceTableData,
												llmPayload.columns,
												newType as 'barchart' | 'columnchart' | 'linechart' | 'piechart',
												{ dimensionColumn: dimensionCol, secondaryDimensionColumn: selectedSecondaryDimension, measureColumns: measureCols }
											);
											if (transformedData) {
												onChartTypeChangeWithData(newType, transformedData);
											}
										}
									}}
									onEditClick={() => {
										setIsChartConfigDrawerOpen(true);
									}}
								/>
							)}
							{/* Fallback ChartSwitcher for regular chart views (without table source data) */}
							{showChartSwitcher && chartSwitcherCurrentType && chartSwitcherCurrentType !== 'linechart' && chartSwitcherData && !hasTableSourceData() && llmPayload?.columns && getDimensionColumns(llmPayload.columns).length > 0 && (
								<ChartSwitcher
									defaultType={chartSwitcherDefaultType}
									currentType={chartSwitcherCurrentType}
									data={chartSwitcherData}
									onTypeChange={onChartTypeChange || (() => { })}
								/>
							)}

							<Tooltip message="View SQL">
								<button
									title="btnopensqldrawer"
									onClick={() => setShowDetails(!showDetails)}
									type="button"
									className={`p-2 group flex items-center justify-center rounded-full transition-colors
									${showDetails
											? 'text-primary'
											: 'text-tertiary-text hover:bg-secondary-background hover:border-tertiary-background hover:text-primary'
										}`}
								>
									<SquareCode className="w-5 h-5" strokeWidth={1.75} />
								</button>
							</Tooltip>

							{/* <Tooltip message="Chat with the result">
								<button
									title="btnopendrawer"
									onClick={onDrawerOpen}
									type="button"
									className="p-2 group flex items-center justify-center rounded-full transition-colors text-tertiary-text hover:bg-secondary-background hover:text-primary"
								>
									<MessageSquare className="w-5 h-5" strokeWidth={1.75}/>
								</button>
							</Tooltip> */}
						</>
					)}

					{pdfGeneration && <PDFDownload messageId={messageId} filterData={filterData} onDownload={onDownloadPDF} />}
					<DownloadAction onDownload={exportHandler} />
					{/* MenuAction (Expand) - Always at the end */}
					<MenuAction
						onFullscreen={() => {
							if (isInModal) {
								if (onFullView) onFullView();
							} else if (chartType === 'table') {
								// Tables use TableFullViewModal
								handleTableFullView();
							} else if (onChartFullViewOpen && chartType) {
								// Charts use ChartFullViewModal (removed vizKey requirement)
								handleChartFullView();
							} else {
								if (onFullView) onFullView();
							}
						}}
						forceCollapseState={isInModal}
						externalState={false}
					/>
				</div>
			</div>

			{/* ChartConfigDrawer for configuring chart columns */}
			{hasTableSourceData() && llmPayload?.columns && (
				<ChartConfigDrawer
					isOpen={isChartConfigDrawerOpen}
					onClose={() => {
						setIsChartConfigDrawerOpen(false);
					}}
					columns={llmPayload.columns}
					selectedDimension={selectedDimension}
					selectedSecondaryDimension={selectedSecondaryDimension}
					selectedMeasures={selectedMeasures}
					chartType={selectedChartType !== 'table' ? selectedChartType : 'barchart'}
					onApply={(chartTypeFromDrawer, dimension, measures, secondaryDimension) => {
						setSelectedChartType(chartTypeFromDrawer);
						setSelectedDimension(dimension);
						setSelectedSecondaryDimension(secondaryDimension || '');
						setSelectedMeasures(measures);
						setIsChartConfigDrawerOpen(false);

						const sourceTableData = getSourceTableData();
						if (sourceTableData && onChartTypeChangeWithData && llmPayload?.columns) {
							const transformedData = transformTableToChartData(
								sourceTableData,
								llmPayload.columns,
								chartTypeFromDrawer as 'barchart' | 'columnchart' | 'linechart' | 'piechart',
								{ dimensionColumn: dimension, secondaryDimensionColumn: secondaryDimension, measureColumns: measures }
							);
							if (transformedData) {
								onChartTypeChangeWithData(chartTypeFromDrawer, transformedData);
							}
						}
					}}
				/>
			)
			}


			{
				!onFilterDrawerOpen && (
					<FilterDrawer
						isOpen={isFilterDrawerOpen}
						onClose={() => setIsFilterDrawerOpen(false)}
						availableColumns={availableColumns}
						filterDataMap={filterDataMap}
						existingFilters={convertAppliedFiltersToFilterRows()}
						onApply={handleApplyFiltersFromDrawer}
						isLoading={isApplyingFilters || loadingColumns}
					/>
				)
			}

			{showDetails && sqlQuery && (
				<div className="mt-2 mb-5 p-3 md:p-5 bg-[#f8f9fa] rounded-[12px] md:rounded-[28px] border border-gray-200 overflow-hidden w-full max-w-[calc(100vw-50px)] md:max-w-full">
					<div className="overflow-x-auto overflow-y-auto max-h-52 w-full">
					<pre className="text-xs text-gray-800 whitespace-pre-wrap break-words font-mono leading-relaxed min-w-0 max-w-full">
						{formatSQLQuery(sqlQuery)}
					</pre>
					</div>
				</div>
			)}
		</div >
	);
};

export default AppliedFiltersDisplay;